/**
 * "Save as custom report" for a report built on the dashboard.
 *
 * MOVE (the default) points the tile at the new custom report, so edits to it
 * reach the tile from then on. COPY leaves the tile as it is, and the two part
 * ways. Either way the new report starts private: the dashboard's recipients
 * still see the tile, and nobody else sees the report until it is shared.
 *
 * It acts on the SAVED dashboard — see `DashboardService.promoteTile` — so it
 * is refused while this tile has unsaved changes, rather than silently saving
 * the version everybody else sees instead of the one on screen.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export type PromoteMode = 'move' | 'copy';

interface Props {
  open: boolean;
  /** The tile's title — the default name. */
  title: string;
  /** The tile differs from the saved dashboard; promoting would act on the other one. */
  unsaved: boolean;
  /** The dashboard's bar is filtering something, which the report won't carry. */
  barFiltered: boolean;
  busy: boolean;
  error: string | null;
  onSubmit: (input: { name: string; mode: PromoteMode }) => void;
  onOpenChange: (open: boolean) => void;
}

const MODES: { value: PromoteMode; label: string; help: string }[] = [
  {
    value: 'move',
    label: 'Move it',
    help: 'The tile shows the custom report from now on, so edits to the report show here.',
  },
  {
    value: 'copy',
    label: 'Copy it',
    help: 'The tile keeps its own report. The two can change separately.',
  },
];

export function PromoteTileDialog({
  open,
  title,
  unsaved,
  barFiltered,
  busy,
  error,
  onSubmit,
  onOpenChange,
}: Props) {
  const [name, setName] = useState(title);
  const [mode, setMode] = useState<PromoteMode>('move');
  const blank = name.trim() === '';

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!blank && !unsaved) onSubmit({ name: name.trim(), mode });
          }}
        >
          <DialogHeader>
            <DialogTitle>Save as custom report</DialogTitle>
            <DialogDescription>
              It’ll be listed in your Custom Reports, private to you until you share it.
            </DialogDescription>
          </DialogHeader>

          {unsaved ? (
            <p role="alert" className="rounded-md border bg-muted px-3 py-2 text-sm">
              This tile has unsaved changes. Save the dashboard first, then try again.
            </p>
          ) : (
            <>
              <div className="grid gap-2">
                <Label htmlFor="promote-name">Name</Label>
                <Input
                  id="promote-name"
                  value={name}
                  maxLength={120}
                  autoFocus
                  onChange={(e) => setName(e.target.value)}
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? 'promote-error' : undefined}
                />
                {error && (
                  <p id="promote-error" role="alert" className="text-destructive text-xs">
                    {error}
                  </p>
                )}
              </div>

              <fieldset className="grid gap-2">
                <legend className="mb-1 font-medium text-sm">This tile</legend>
                {MODES.map((option) => (
                  <label
                    key={option.value}
                    className="flex cursor-pointer items-start gap-2 rounded-md border p-3 has-[:checked]:border-primary"
                  >
                    <input
                      type="radio"
                      name="promote-mode"
                      value={option.value}
                      checked={mode === option.value}
                      onChange={() => setMode(option.value)}
                      className="mt-0.5"
                    />
                    <span className="grid gap-0.5">
                      <span className="font-medium text-sm">{option.label}</span>
                      <span className="text-muted-foreground text-xs">{option.help}</span>
                    </span>
                  </label>
                ))}
              </fieldset>

              {barFiltered && (
                <p className="text-muted-foreground text-xs">
                  Filters set on the dashboard’s bar aren’t included. The report keeps its own
                  filters and the dashboard’s period.
                </p>
              )}
            </>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              disabled={busy}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={busy || blank || unsaved}>
              {busy ? 'Saving…' : 'Save report'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
